/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';

import { ApiStatus } from '../../../../ts';
import { ConvoSkillsSlice } from '../../../../ts/state';
import {
  createConvoSkillAction,
  getConvoSkill,
  listConvoSkills,
  updateConvoSkillAction
} from './actions';
import { convoSkillAdapter } from './adapter';

const initialState: ConvoSkillsSlice = convoSkillAdapter.getInitialState({
  listConvoSkills: {
    status: ApiStatus.IDLE,
    fetched: null,
    error: null
  },
  getConvoSkill: {
    status: ApiStatus.IDLE,
    fetched: null,
    error: null
  },
  createConvoSkillAction: {
    status: ApiStatus.IDLE,
    fetched: null,
    error: null
  },
  updateConvoSkillAction: {
    status: ApiStatus.IDLE,
    fetched: null,
    error: null
  },
  draft: null,
  isDirty: false,
  isNew: false,
  providerId: null
});

/**
 * Gets the initial state of the redux slice and apply updates.
 * @param {*} updates The updates to apply to the initial state
 * @returns {*} The slice initial state
 */
export const computeState = (
  updates?: Partial<ConvoSkillsSlice>
): ConvoSkillsSlice => ({
  ..._.cloneDeep(initialState),
  ...updates
});

const slice = createSlice({
  name: 'convoSkills',
  initialState: computeState(),
  reducers: {
    setConvoSkillDraft(state: ConvoSkillsSlice, action) {
      const { skill, isDirty } = action.payload;
      state.draft = skill || null;
      state.isDirty = isDirty;
    },
    setDraftConditions(state: ConvoSkillsSlice, action) {
      state.draft.conditions = action.payload;
      state.isDirty = true;
    },
    // This sets the "global" provider ID (to support the case where there is
    // a single conversational skill provider in the instance payload
    setConvoSkillProviderId(state: ConvoSkillsSlice, action) {
      state.providerId = action.payload;
    },
    // This sets in the current draft the provider ID of the currently-selected
    // conversational skill (to support the case where there are several
    // conversational skill providers in the instance payload)
    setDraftProviderId(state: ConvoSkillsSlice, action) {
      state.draft.providerId = action.payload;
    },
    setDraftSkillId(state: ConvoSkillsSlice, action) {
      state.draft.convoSkillId = action.payload;
      state.isDirty = true;
    },
    setDraftSkillRevision(state: ConvoSkillsSlice, action) {
      state.draft.convoSkillRevision = action.payload;
      state.isDirty = true;
    },
    setDraftActionId(state: ConvoSkillsSlice, action) {
      state.draft.actionId = action.payload;
      state.isDirty = true;
    },
    setDraftIntentId(state: ConvoSkillsSlice, action) {
      state.draft.intentId = action.payload;
      state.isDirty = true;
    },
    setDraftDescription(state: ConvoSkillsSlice, action) {
      state.draft.description = action.payload;
      state.isDirty = true;
    },
    setDraftResponseMode(state: ConvoSkillsSlice, action) {
      state.draft.responseMode = action.payload;
      state.isDirty = true;
    },
    setDraftIsDisambiguationEnabled(state: ConvoSkillsSlice, action) {
      state.draft.isDisambiguationEnabled = action.payload;
      state.isDirty = true;
    },
    setDraftIsChangeTopicEnabled(state: ConvoSkillsSlice, action) {
      state.draft.isChangeTopicEnabled = action.payload;
      state.isDirty = true;
    },
    setDraftIsNeverReturnEnabled(state: ConvoSkillsSlice, action) {
      state.draft.isNeverReturnEnabled = action.payload;
      state.isDirty = true;
    },
    setDraftDisplayName(state: ConvoSkillsSlice, action) {
      state.draft.displayName = action.payload;
      state.isDirty = true;
    },
    setDraftName(state: ConvoSkillsSlice, action) {
      state.draft.name = action.payload;
      state.isDirty = true;
    },
    setDraftNextActionId(state: ConvoSkillsSlice, action) {
      state.draft.nextAction = action.payload;
      state.isDirty = true;
    },
    setDraftExamples(state: ConvoSkillsSlice, action) {
      state.draft.examples = action.payload;
      state.isDirty = true;
    },
    setDraftIsDirty(state: ConvoSkillsSlice, action) {
      state.isDirty = action.payload;
    },
    setDraftIsNew(state: ConvoSkillsSlice, action) {
      state.isNew = action.payload;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(listConvoSkills.pending, state => {
        state.listConvoSkills.error = null;
        state.listConvoSkills.status = ApiStatus.LOADING;
      })
      .addCase(listConvoSkills.fulfilled, (state, action) => {
        state.listConvoSkills.status = ApiStatus.SUCCEEDED;
        state.listConvoSkills.fetched = Date.now();
        state.listConvoSkills.error = null;
        convoSkillAdapter.setAll(state, action.payload);
      })
      .addCase(listConvoSkills.rejected, (state, action) => {
        state.listConvoSkills.status = ApiStatus.ERROR;
        state.listConvoSkills.fetched = null;
        state.listConvoSkills.error = action.error;
      })
      .addCase(getConvoSkill.pending, state => {
        state.getConvoSkill.error = null;
        state.getConvoSkill.status = ApiStatus.LOADING;
      })
      .addCase(getConvoSkill.fulfilled, (state, action) => {
        state.getConvoSkill.status = ApiStatus.SUCCEEDED;
        state.getConvoSkill.fetched = Date.now();
        state.getConvoSkill.error = null;
        convoSkillAdapter.upsertOne(state, action.payload);
      })
      .addCase(getConvoSkill.rejected, (state, action) => {
        state.getConvoSkill.status = ApiStatus.ERROR;
        state.getConvoSkill.fetched = null;
        state.getConvoSkill.error = action.error;
      })
      .addCase(createConvoSkillAction.pending, state => {
        state.createConvoSkillAction.error = null;
        state.createConvoSkillAction.status = ApiStatus.LOADING;
      })
      .addCase(createConvoSkillAction.fulfilled, state => {
        state.createConvoSkillAction.status = ApiStatus.SUCCEEDED;
        state.createConvoSkillAction.fetched = Date.now();
        state.createConvoSkillAction.error = null;
      })
      .addCase(createConvoSkillAction.rejected, (state, action) => {
        state.createConvoSkillAction.status = ApiStatus.ERROR;
        state.createConvoSkillAction.fetched = Date.now();
        state.createConvoSkillAction.error = action.error;
      })
      .addCase(updateConvoSkillAction.pending, state => {
        state.updateConvoSkillAction.error = null;
        state.updateConvoSkillAction.status = ApiStatus.LOADING;
      })
      .addCase(updateConvoSkillAction.fulfilled, state => {
        state.updateConvoSkillAction.status = ApiStatus.SUCCEEDED;
        state.updateConvoSkillAction.fetched = Date.now();
        state.updateConvoSkillAction.error = null;
      })
      .addCase(updateConvoSkillAction.rejected, (state, action) => {
        state.updateConvoSkillAction.status = ApiStatus.ERROR;
        state.updateConvoSkillAction.fetched = Date.now();
        state.updateConvoSkillAction.error = action.error;
      });
  }
});

export const {
  setConvoSkillDraft,
  setConvoSkillProviderId,
  setDraftProviderId,
  setDraftActionId,
  setDraftIntentId,
  setDraftDescription,
  setDraftExamples,
  setDraftResponseMode,
  setDraftDisplayName,
  setDraftName,
  setDraftNextActionId,
  setDraftConditions,
  setDraftSkillId,
  setDraftSkillRevision,
  setDraftIsDirty,
  setDraftIsNew,
  setDraftIsDisambiguationEnabled,
  setDraftIsChangeTopicEnabled,
  setDraftIsNeverReturnEnabled
} = slice.actions;

export default slice.reducer;
